// import { useQueryClient } from '@tanstack/react-query';
import { RliSdk } from '@rocketlane/rli-internal-sdk';
import { useNavigate } from '@rocketlane/rocket-router';
import { fromPairs, toPairs } from 'lodash';
import { parse, ParsedQuery, stringify } from 'query-string';
import { useCallback, useMemo } from 'react';
import { ProjectId, TaskIdV2 } from 'api/models';
// import { TaskAPI } from 'api/tasks';
import { TaskEvents, TaskEventsMap } from 'events/tasks';
import { useHash } from 'hooks/use-hash';
// import { getTaskDetailQueryKey } from 'queries/task';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useUserType } from 'hooks/use-user-type';
import { logger } from 'services';
import { TaskDrawerParams, createTransformer } from './transformer';

type ShowFunction = (
  project: ProjectId,
  task: TaskIdV2,
  tab?: TaskDrawerTab,
) => void;
type HideFunction = () => void;

const sanitize = (value: ParsedQuery): ParsedQuery =>
  fromPairs(toPairs(value).filter(([, x]) => x != null)) as ParsedQuery;

/**
 * Returns only show/hide — does NOT subscribe to hash. Use in components that
 * only need to open/close the drawer (e.g. TaskCardV2). Avoids re-renders when
 * drawer opens/closes.
 */
export const useShowTaskDrawer = (): [ShowFunction, HideFunction] => {
  const navigate = useNavigate();
  const userType = useUserType();
  const transformer = useMemo(
    () => createTransformer({ userType }),
    [userType],
  );

  const show = useCallback(
    (project: ProjectId, task: TaskIdV2, tab?: TaskDrawerTab) => {
      if (typeof window === 'undefined') {
        return;
      }
      const { search } = window.location;
      const currentHash = window.location.hash.slice(1);
      const parsed = parse(currentHash || '');
      const nextParams = transformer.to(['show', project, task, tab]);
      const merged = sanitize({ ...parsed, ...nextParams });
      const nextHash = `#${stringify(merged)}`;

      navigate(
        { hash: nextHash, search },
        { replace: false, state: window.history.state?.usr },
      );
      try {
        RliSdk.emitToAllWidgets(TaskEventsMap[TaskEvents.OpenTaskDrawer], {
          projectId: project,
          taskId: task,
        });
      } catch (error) {
        logger.error('Failed to publish open task drawer event', { error });
      }
    },
    [navigate, transformer],
  );

  const hide = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const { search } = window.location;
    const currentHash = window.location.hash.slice(1);
    const parsed = parse(currentHash || '');
    const nextParams = transformer.to(['invalid']);
    const merged = sanitize({ ...parsed, ...nextParams });
    const nextHash = `#${stringify(merged)}`;

    navigate(
      { hash: nextHash, search },
      { replace: false, state: window.history.state?.usr },
    );
  }, [navigate, transformer]);

  return [show, hide];
};

export const useTaskDrawer = (): [
  TaskDrawerParams,
  ShowFunction,
  HideFunction,
] => {
  const userType = useUserType();
  const transformer = useMemo(
    () => createTransformer({ userType }),
    [userType],
  );
  const [params, setParams] = useHash(transformer);
  // const queryClient = useQueryClient();
  const show = useCallback(
    (project: ProjectId, task: TaskIdV2, tab?: TaskDrawerTab) => {
      setParams(['show', project, task, tab]);
      try {
        RliSdk.emitToAllWidgets(TaskEventsMap[TaskEvents.OpenTaskDrawer], {
          projectId: project,
          taskId: task,
        });
      } catch (error) {
        logger.error('Failed to publish open task drawer event', { error });
      }
    },
    [setParams],
  );
  const hide = useCallback(() => {
    setParams(['invalid']);
  }, [setParams]);

  return [params, show, hide];
};
